import { groupActivityByMonth, isFinishedTask, splitImportedBody, activityDate } from "./presentation";
import { useState, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { Select } from "antd";
import UIkit from "uikit";
import feather from "feather-icons";
import CRMService from "../../services/crm.service";
import { AppContexts } from "../../providers";
import Dialog from "../common/Dialog";
import { formatTaskStatus } from "./activity-utils";

const FeatherIcon = ({ name, size = 16, className = "" }) => {
  const icon = feather.icons[name];
  if (!icon) return null;
  return (
    <svg
      className={`feather-icon ${className}`}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      dangerouslySetInnerHTML={{ __html: icon.contents }}
    />
  );
};

const ACTIVITY_ICONS = {
  note: "file-text",
  task: "check-square",
  call: "phone",
  email: "mail",
  event: "calendar",
  text_message: "message-square",
  contact_created: "user-plus",
};

const ACTIVITY_LABELS = {
  note: "Note",
  task: "Task",
  call: "Call",
  email: "Email",
  event: "Event",
  text_message: "Message",
  contact_created: "Created",
};

// Parse a date string from the API. If it lacks timezone info (e.g.
// "2026-05-07 14:00:00" returned by some Postgres timestamps without TZ),
// treat it as UTC so it converts to the user's local time consistently with
// the messaging surface. Native ISO strings (with Z or +hh:mm) are passed
// through unchanged.
const parseApiDate = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return dateStr;
  let s = String(dateStr);
  // "YYYY-MM-DD HH:MM:SS" (no T, no zone) → assume UTC
  const tzLess = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;
  if (tzLess.test(s)) {
    s = s.replace(" ", "T") + "Z";
  }
  return new Date(s);
};

const formatDateTime = (dateStr) => {
  const d = parseApiDate(dateStr);
  if (!d || isNaN(d.getTime())) return "";
  // MM/DD/YYYY h:mm AM/PM
  return `${d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  })} ${d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  })}`;
};

const formatDate = (dateStr) => {
  const d = parseApiDate(dateStr);
  if (!d || isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

// Format a calendar date that should NOT shift across timezones.
// Accepts "YYYY-MM-DD" or full ISO; renders the date components verbatim
// without applying a timezone conversion (avoids off-by-one display).
const formatPlainDate = (dateStr) => {
  if (!dateStr) return "";
  const s = String(dateStr).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return formatDate(dateStr);
  return `${m[2]}/${m[3]}/${m[1]}`;
};

const formatDuration = (seconds) => {
  if (!seconds) return "";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

// ─── Resolve entity_type + entity_id from a CRM contact ───
const getEntity = (contact) => {
  if (!contact) return { entityType: null, entityId: null };
  if (contact._type === "unit") {
    return { entityType: "unit", entityId: contact.rental_id };
  }
  if (contact._type === "guest") {
    return {
      entityType: "guest",
      entityId: contact.guest_id || contact.crm_identity_id || contact.id,
    };
  }
  const cat = (contact.category || "").toLowerCase();
  const entityType = cat === "lead" || cat === "leads" ? "lead" : "contact";
  return { entityType, entityId: contact.contact_id || contact.id };
};

const DEFAULT_MODE = "activity";
const TIMELINE_PAGE_SIZE = 50;

const normalizeActivityType = (item) => {
  const raw = String(item?.activity_type || item?.type || "").toLowerCase();
  if (raw === "call" || raw === "phone_call" || raw === "phone-call") return "call";
  if (raw === "email" || raw === "crm_email" || raw === "mail") return "email";
  if (raw === "text_message" || raw === "text" || raw === "message" || raw === "sms" || raw === "sms_message") return "text_message";
  if (raw === "contact_created" || raw === "contact-created" || raw === "created") return "contact_created";
  if (raw === "note") return "note";
  if (raw === "event") return "event";
  if (raw === "task") return "task";
  if (!raw && item?.sender_type && item?.content) return "text_message";
  return raw || "note";
};

const filterItemsForMode = (mode, items) => {
  if (mode === "note") {
    return items.filter((i) => normalizeActivityType(i) === "note");
  }
  if (mode === "event") {
    return items.filter((i) => normalizeActivityType(i) === "event");
  }
  if (mode === "task") {
    return items.filter((i) => normalizeActivityType(i) === "task");
  }
  // activity mode: calls + texts + system-created events only
  return items.filter((i) => (
    normalizeActivityType(i) === "call"
    || normalizeActivityType(i) === "email"
    || normalizeActivityType(i) === "text_message"
    || normalizeActivityType(i) === "contact_created"
  ));
};

// ─── Activity Card ───
const ActivityCard = ({ item, onEdit, onDelete }) => {
  const icon = ACTIVITY_ICONS[item.activity_type] || "activity";
  const label = ACTIVITY_LABELS[item.activity_type] || item.activity_type;
  const isMessage = item.activity_type === "text_message" || item.activity_type === "contact_created";

  return (
    <div className={`crm-activity-card ${item.activity_type || ""}`}>
      <div className={`crm-activity-card-icon ${item.activity_type}`}>
        <FeatherIcon name={icon} size={14} />
      </div>
      <div className="crm-activity-card-body">
        <div className="crm-activity-card-header">
          <span className="crm-activity-card-type">{label}</span>
          {item.sender_type && (
            <span className={`crm-activity-card-direction ${item.sender_type}`}>
              {item.sender_type === "recipient" ? "← Inbound" : item.sender_type === "user" ? "→ Outbound" : item.sender_type === "automated" ? "→ Auto" : ""}
            </span>
          )}
        </div>
        <div className="crm-activity-card-title">
          <TimelineMessage content={item.title || item.content || "(no content)"} />
        </div>
        {item.description && (
          <div className="crm-activity-card-desc"><TimelineMessage content={item.description} /></div>
        )}
        {/* Task meta */}
        {item.activity_type === "task" && (
          <div className="crm-activity-card-meta">
            {item.status && (
              <span className={`crm-activity-status ${item.status}`}>
                {formatTaskStatus(item.status)}
              </span>
            )}
            {item.due_at && (
              <span className="crm-activity-due">Due {formatPlainDate(item.due_at)}</span>
            )}
          </div>
        )}
        {/* Call meta */}
        {item.activity_type === "call" && (
          <div className="crm-activity-card-meta">
            {item.direction && (
              <span className="crm-activity-direction">{item.direction}</span>
            )}
            {item.outcome && (
              <span className="crm-activity-outcome">{item.outcome}</span>
            )}
            {item.duration_seconds > 0 && (
              <span className="crm-activity-duration">{formatDuration(item.duration_seconds)}</span>
            )}
          </div>
        )}
        {item.activity_type === "email" && (
          <div className="crm-activity-card-meta">
            {item.direction && (
              <span className="crm-activity-direction">{item.direction}</span>
            )}
            {item.to_email && (
              <span className="crm-activity-outcome">To: {item.to_email}</span>
            )}
            {item.cc_email && (
              <span className="crm-activity-outcome">CC: {item.cc_email}</span>
            )}
          </div>
        )}
        {/* Event meta */}
        {item.activity_type === "event" && item.event_date && (
          <div className="crm-activity-card-meta">
            <span className="crm-activity-event-date">
              <FeatherIcon name="calendar" size={12} /> {formatPlainDate(item.event_date)}
            </span>
          </div>
        )}
        <div className="crm-activity-card-time">{formatDateTime(item.created_at)}</div>
      </div>
      <div className="crm-activity-card-actions">
        {onEdit && !isMessage && (
          <button className="crm-activity-action-btn" onClick={() => onEdit(item)} uk-tooltip="Edit">
            <FeatherIcon name="edit-2" size={13} />
          </button>
        )}
        {onDelete && !isMessage && (
          <button className="crm-activity-action-btn" onClick={() => onDelete(item)} uk-tooltip="Delete">
            <FeatherIcon name="trash-2" size={13} />
          </button>
        )}
      </div>
    </div>
  );
};

const TimelineMessage = ({ content }) => {
  const { body, details } = splitImportedBody(content);
  const [expanded, setExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const textRef = useCallback((node) => {
    if (node) {
      setIsClamped(node.scrollHeight > node.clientHeight);
    }
  }, [content]);

  return (
    <>
      <div
        ref={textRef}
        className={`crm-timeline-text ${expanded ? "expanded" : "clamped"}`}
      >
        {body}
      </div>
      {details && <details className="crm-import-details"><summary>Imported details</summary>{details}</details>}
      {isClamped && !expanded && (
        <button
          className="crm-timeline-show-more"
          onClick={() => setExpanded(true)}
        >
          Show more
        </button>
      )}
      {expanded && (
        <button
          className="crm-timeline-show-more"
          onClick={() => setExpanded(false)}
        >
          Show less
        </button>
      )}
    </>
  );
};

const ActivityTimelineItem = ({ item, onEdit, onDelete }) => {
  const isMessage = item.activity_type === "text_message" || item.activity_type === "contact_created";
  const directionClass = item.activity_type === "email"
    ? (item.direction === "inbound" ? "inbound" : "outbound")
    : (item.sender_type === "recipient"
      ? "inbound"
      : (item.sender_type === "user" || item.sender_type === "automated" ? "outbound" : ""));

  const title = item.activity_type === "call"
    ? (item.title || item.summary || "Call")
    : item.activity_type === "email"
      ? (item.subject || item.title || "Email")
    : (item.title || item.content || "");

  return (
    <div className={`crm-timeline-item ${item.activity_type || ""} ${directionClass}`}>
      <div className="crm-timeline-line" />
      <div className={`crm-timeline-icon ${item.activity_type || "note"}`}>
        <FeatherIcon name={ACTIVITY_ICONS[item.activity_type] || "bell"} size={14} />
      </div>
      <div className="crm-timeline-content">
        <div className="crm-timeline-header">
          <span className="crm-timeline-sender">
            {item.activity_type === "call"
              ? "Call"
              : item.activity_type === "email"
                ? "Email"
                : item.activity_type === "contact_created"
                  ? "System"
                  : "Message"}
          </span>
        </div>
        {item.activity_type === "call" ? (
          <div className="crm-timeline-text expanded">{title}</div>
        ) : item.activity_type === "email" ? (
          <strong className="crm-email-subject">{title}</strong>
        ) : (
          <TimelineMessage content={title} />
        )}
        {item.activity_type === "email" && item.body && (
          <TimelineMessage content={item.body} />
        )}
        {item.activity_type === "call" && (
          <div className="crm-activity-card-meta">
            {item.direction && <span className="crm-activity-direction">{item.direction}</span>}
            {item.outcome && <span className="crm-activity-outcome">{item.outcome}</span>}
            {item.duration_seconds > 0 && (
              <span className="crm-activity-duration">{formatDuration(item.duration_seconds)}</span>
            )}
          </div>
        )}
        {item.activity_type === "email" && (
          <div className="crm-activity-card-meta">
            {item.direction && <span className="crm-activity-direction">{item.direction}</span>}
            {item.to_email && <span className="crm-activity-outcome">To: {item.to_email}</span>}
            {item.cc_email && <span className="crm-activity-outcome">CC: {item.cc_email}</span>}
          </div>
        )}
        <div className="crm-timeline-time">{formatDateTime(item.sent_at || item.created_at)}</div>
        {!isMessage && (
          <div className="crm-activity-card-actions">
            {onEdit && (
              <button className="crm-activity-action-btn" onClick={() => onEdit(item)} uk-tooltip="Edit">
                <FeatherIcon name="edit-2" size={13} />
              </button>
            )}
            {onDelete && (
              <button className="crm-activity-action-btn" onClick={() => onDelete(item)} uk-tooltip="Delete">
                <FeatherIcon name="trash-2" size={13} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main CRM Activity Panel ───
const CRMActivityPanel = ({
  contact,
  mode = DEFAULT_MODE,
  subscriptionLocked = false,
  lockedNotice = null,
  readOnly = false,
}) => {
  const isUnit = contact?._type === "unit";
  const isGuest = contact?._type === "guest";
  const isContact = !isUnit && !isGuest;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [timelinePage, setTimelinePage] = useState(0);
  const [hasMoreTimeline, setHasMoreTimeline] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [taskFilter, setTaskFilter] = useState("all");

  // Offcanvas state
  const [formType, setFormType] = useState(null); // note, task, call, email, event
  const [editingItem, setEditingItem] = useState(null);

  const { entityType, entityId } = getEntity(contact);
  // CRM activity tables historically store lead records under "contact"
  const activityEntityType = entityType === "lead" ? "contact" : entityType;

  const dedupeSort = useCallback((arr) => {
    const uniq = [];
    const seen = new Set();
    arr.forEach((item) => {
      const normalizedType = normalizeActivityType(item);
      const key = `${normalizedType}-${item.id || item.created_at}-${item.title || item.content || ""}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniq.push({ ...item, activity_type: normalizedType });
      }
    });
    uniq.sort((a, b) => (activityDate(b)?.getTime() || 0) - (activityDate(a)?.getTime() || 0));
    return uniq;
  }, []);

  // Fetch data based on mode
  const fetchData = useCallback(async () => {
    if (subscriptionLocked) {
      setItems([]);
      setLoading(false);
      setHasMoreTimeline(false);
      return;
    }

    if (!activityEntityType || !entityId) return;
    if (timelinePage === 0 || mode !== "activity") {
      setLoading(true);
    }
    try {
      if (mode === "note") {
        const res = await CRMService.getNotes(activityEntityType, entityId);
        const data = res?.data || [];
        setItems(data.map((n) => ({ ...n, activity_type: "note", title: n.body })));
        setLoading(false);
        return;
      }

      if (mode === "event") {
        const res = await CRMService.getEvents(activityEntityType, entityId);
        const data = res?.data || [];
        setItems(data.map((e) => ({ ...e, activity_type: "event" })));
        setLoading(false);
        return;
      }

      if (mode === "task") {
        const res = await CRMService.getTasks(activityEntityType, entityId);
        const data = res?.data || [];
        setItems(data.map((t) => ({ ...t, activity_type: "task" })));
        setLoading(false);
        return;
      }

      // activity mode
      let calls = [];
      let emails = [];
      if (timelinePage === 0 || !isContact) {
        const callsRes = await CRMService.getCalls(activityEntityType, entityId);
        calls = (callsRes?.data || []).map((c) => ({
          ...c,
          activity_type: normalizeActivityType({ activity_type: c.activity_type || "call" }),
          title: c.summary,
        }));

        const emailsRes = await CRMService.getEmails(activityEntityType, entityId);
        emails = (emailsRes?.data || []).map((e) => ({
          ...e,
          activity_type: normalizeActivityType({ activity_type: e.activity_type || "email" }),
          title: e.subject,
        }));
      }

      let tlItems = [];

      if (isContact || isGuest) {
        try {
          const timelineId = isGuest
            ? (contact.guest_id || contact.id)
            : (contact.contact_id || contact.id);
          const fetchTimeline = isGuest
            ? CRMService.getGuestTimeline
            : CRMService.getContactTimeline;

          const tlRes = await fetchTimeline(timelineId, {
            per: TIMELINE_PAGE_SIZE,
            page: timelinePage,
          });
          tlItems = (tlRes?.data || []).map((t) => ({
            ...t,
            activity_type: normalizeActivityType({ type: t.type, sender_type: t.sender_type, content: t.content }),
            title: t.content,
          }));
          setHasMoreTimeline(tlItems.length === TIMELINE_PAGE_SIZE);
        } catch (_) {
          // show CRM activities only if timeline fetch fails
          setHasMoreTimeline(false);
        }
      } else {
        setHasMoreTimeline(false);
      }

      if (timelinePage === 0) {
        const next = dedupeSort([...calls, ...emails, ...tlItems]);
        setItems(filterItemsForMode("activity", next));
      } else {
        setItems((prev) => {
          const next = dedupeSort([...prev, ...tlItems]);
          return filterItemsForMode("activity", next);
        });
      }
    } catch (err) {
      console.error("Failed to fetch CRM activities", err);
      setItems([]);
    }
    setLoading(false);
  }, [
    activityEntityType,
    entityId,
    isContact,
    contact,
    mode,
    subscriptionLocked,
    timelinePage,
    dedupeSort,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setTimelinePage(0);
  }, [contact?.id, contact?.contact_id, contact?.guest_id, contact?.rental_id, mode]);

  const openForm = useCallback((type, item = null) => {
    if (readOnly) return;
    setFormType(type);
    setEditingItem(item);
    // Small delay to ensure state is set before UIkit opens
    setTimeout(() => UIkit.offcanvas("#offcanvas-crm-activity-form").show(), 10);
  }, [readOnly]);

  const handleEdit = useCallback((item) => {
    openForm(item.activity_type, item);
  }, [openForm]);

  const openDeleteDialog = useCallback((item) => {
    if (readOnly) return;
    setDeletingItem(item);
    setTimeout(() => UIkit.modal("#modal-crm-activity-delete").show(), 10);
  }, [readOnly]);

  const handleDelete = useCallback(async () => {
    if (readOnly || !deletingItem) return;
    const label = ACTIVITY_LABELS[deletingItem.activity_type] || "Item";
    try {
      if (deletingItem.activity_type === "note") await CRMService.deleteCrmNote(deletingItem.id);
      else if (deletingItem.activity_type === "call") await CRMService.deleteCall(deletingItem.id);
      else if (deletingItem.activity_type === "email") await CRMService.deleteEmail(deletingItem.id);
      else if (deletingItem.activity_type === "event") await CRMService.deleteEvent(deletingItem.id);
      else if (deletingItem.activity_type === "task") await CRMService.deleteTask(deletingItem.id);
      // Optimistic remove
      setItems((prev) => prev.filter((i) => !(i.id === deletingItem.id && i.activity_type === deletingItem.activity_type)));
      UIkit.modal("#modal-crm-activity-delete").hide();
      setDeletingItem(null);
      UIkit.notification(`${label} deleted`, "success");
    } catch (err) {
      console.error("Failed to delete activity", err);
      UIkit.notification("Failed to delete", "error");
    }
  }, [deletingItem, readOnly]);

  const handleFormSave = useCallback(async (type, data, isEdit) => {
    if (readOnly || !activityEntityType || !entityId) return;
    try {
      const payload = { ...data, entity_type: activityEntityType, entity_id: entityId };
      let res;
      if (type === "note") {
        res = isEdit
          ? await CRMService.updateCrmNote(data.id, { body: data.body })
          : await CRMService.createCrmNote(payload);
      } else if (type === "call") {
        res = isEdit
          ? await CRMService.updateCall(data.id, data)
          : await CRMService.createCall(payload);
      } else if (type === "email") {
        res = isEdit
          ? await CRMService.updateEmail(data.id, data)
          : await CRMService.createEmail(payload);
      } else if (type === "event") {
        res = isEdit
          ? await CRMService.updateEvent(data.id, data)
          : await CRMService.createEvent(payload);
      } else if (type === "task") {
        res = isEdit
          ? await CRMService.updateTask(data.id, data)
          : await CRMService.createTask(payload);
      }

      const saved = res?.data;
      if (saved) {
        // Normalize the saved item
        let normalized = { ...saved, activity_type: type };
        if (type === "note") normalized.title = saved.body;
        if (type === "call") normalized.title = saved.summary;
        if (type === "email") normalized.title = saved.subject;

        if (isEdit) {
          setItems((prev) =>
            prev.map((i) => (i.id === saved.id && i.activity_type === type ? normalized : i)),
          );
        } else {
          setItems((prev) => [normalized, ...prev]);
        }
      }

      UIkit.offcanvas("#offcanvas-crm-activity-form").hide();
      UIkit.notification(isEdit ? "Updated" : "Added", "success");
      // Clear form state so reopening "Add" starts blank instead of showing
      // the previously entered values.
      setFormType(null);
      setEditingItem(null);
    } catch (err) {
      console.error("Failed to save activity", err);
      UIkit.notification("Failed to save", "error");
    }
  }, [readOnly, activityEntityType, entityId]);

  const canAddCall = mode === "activity" && !isUnit;
  const canAddEmail = mode === "activity" && !isUnit;
  const canAddNote = mode === "note";
  const canAddEvent = mode === "event" && !isUnit;
  const canAddTask = mode === "task";

  return (
    <div className="crm-activity-panel">
      {subscriptionLocked && lockedNotice}

      {/* Add button */}
      <div className="crm-activity-add-bar">
        {!readOnly && canAddCall && (
          <button
            className="uk-button hms-btn hms-white-btn uk-button-small"
            onClick={() => openForm("call")}
            disabled={subscriptionLocked}
            title={subscriptionLocked ? "CRM Subscription Required" : undefined}
          >
            <FeatherIcon name="plus" size={14} /> Call
          </button>
        )}
        {!readOnly && canAddEmail && (
          <button
            className="uk-button hms-btn hms-white-btn uk-button-small"
            onClick={() => openForm("email")}
            disabled={subscriptionLocked}
            title={subscriptionLocked ? "CRM Subscription Required" : undefined}
          >
            <FeatherIcon name="plus" size={14} /> Email
          </button>
        )}
        {!readOnly && canAddNote && (
          <button
            className="uk-button hms-btn hms-white-btn uk-button-small"
            onClick={() => openForm("note")}
            disabled={subscriptionLocked}
            title={subscriptionLocked ? "CRM Subscription Required" : undefined}
          >
            <FeatherIcon name="plus" size={14} /> Add Note
          </button>
        )}
        {!readOnly && canAddTask && (
          <button className="uk-button hms-btn hms-white-btn uk-button-small" onClick={() => openForm("task")}>
            <FeatherIcon name="plus" size={14} /> Add Task
          </button>
        )}
        {!readOnly && canAddEvent && (
          <button
            className="uk-button hms-btn hms-white-btn uk-button-small"
            onClick={() => openForm("event")}
            disabled={subscriptionLocked}
            title={subscriptionLocked ? "CRM Subscription Required" : undefined}
          >
            <FeatherIcon name="plus" size={14} /> Add Event
          </button>
        )}
      </div>
      {subscriptionLocked && (
        <div className="crm-list-empty">
          CRM subscription required to view and manage this tab.
        </div>
      )}

      {mode === "task" && !subscriptionLocked && (
        <div className="crm-task-filters" role="group" aria-label="Task status">
          {["all", "open", "finished"].map((filter) => (
            <button key={filter} className="uk-button uk-button-default uk-button-small"
              aria-pressed={taskFilter === filter} onClick={() => setTaskFilter(filter)}>
              {filter === "all" ? "All tasks" : filter === "open" ? "Open tasks" : "Completed / closed"}
            </button>
          ))}
        </div>
      )}
      {/* List */}
      {loading && <div className="crm-list-empty">Loading…</div>}
      {!loading && !subscriptionLocked && items.length === 0 && (
        <div className="crm-list-empty">
          {mode === "note"
            ? "No notes recorded yet"
            : mode === "task"
              ? "No tasks recorded yet"
            : mode === "event"
              ? "No events recorded yet"
              : "No activity recorded yet"}
        </div>
      )}
      {!loading && !subscriptionLocked && mode === "activity" && (
        <div className="crm-timeline">
          {groupActivityByMonth(items).map((group) => (
            <section key={group.key} aria-label={group.label}>
              <h3 className="crm-history-group-title">{group.label}</h3>
              {group.items.map((item, i) => (
                <ActivityTimelineItem key={`${item.activity_type}-${item.id || i}`} item={item}
                  onEdit={readOnly ? null : () => handleEdit(item)} onDelete={readOnly ? null : () => openDeleteDialog(item)} />
              ))}
            </section>
          ))}
          {(isContact || isGuest) && hasMoreTimeline && (
            <div className="crm-timeline-load-more">
              <button
                className="uk-button uk-button-default uk-button-small"
                onClick={() => setTimelinePage((p) => p + 1)}
              >
                Load older messages
              </button>
            </div>
          )}
        </div>
      )}
      {!loading && !subscriptionLocked && mode !== "activity" && (
        mode === "task"
          ? [false, true].filter((finished) => taskFilter === "all" || (taskFilter === "finished") === finished).map((finished) => {
            const tasks = items.filter((item) => isFinishedTask(item) === finished);
            return <section key={String(finished)} aria-label={finished ? "Finished tasks" : "Open tasks"}>
              <h3 className="crm-history-group-title">{finished ? "Completed / closed" : "Open tasks"} ({tasks.length})</h3>
              {tasks.map((item) => <ActivityCard key={item.id} item={item}
                onEdit={readOnly ? null : () => handleEdit(item)} onDelete={readOnly ? null : () => openDeleteDialog(item)} />)}
            </section>;
          })
          : groupActivityByMonth(items).map((group) => <section key={group.key} aria-label={group.label}>
            <h3 className="crm-history-group-title">{group.label}</h3>
            {group.items.map((item, i) => <ActivityCard key={`${item.activity_type}-${item.id || i}`} item={item}
              onEdit={readOnly ? null : () => handleEdit(item)} onDelete={readOnly ? null : () => openDeleteDialog(item)} />)}
          </section>)
      )}

      <ActivityDeleteDialog
        item={deletingItem}
        onDelete={handleDelete}
        onClose={() => setDeletingItem(null)}
      />

      {/* Offcanvas Form */}
      <ActivityFormOffcanvas
        formType={formType}
        editingItem={editingItem}
        taskOwnerValue={
          activityEntityType !== "unit" && activityEntityType !== "guest"
            ? `contact_${entityId}`
            : null
        }
        onSave={handleFormSave}
        onClose={() => {
          setFormType(null);
          setEditingItem(null);
        }}
      />
    </div>
  );
};

const ActivityDeleteDialog = ({ item, onDelete, onClose }) => {
  const label = ACTIVITY_LABELS[item?.activity_type] || "Item";

  return (
    <Dialog id="modal-crm-activity-delete">
      <div className="uk-modal-dialog uk-modal-body">
        <button
          className="uk-modal-close-default"
          type="button"
          uk-close="true"
          onClick={onClose}
        ></button>
        <h2 className="uk-modal-title">Delete {label}</h2>
        <p>
          Are you sure you want to delete <b>{label.toLowerCase()}</b>?
        </p>
        <p className="uk-text-right">
          <button
            className="uk-button hms-btn uk-modal-close uk-modal-delete-cancel"
            type="button"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="uk-button hms-btn hms-gray-btn"
            type="button"
            onClick={onDelete}
          >
            Delete
          </button>
        </p>
      </div>
    </Dialog>
  );
};

// ─── Offcanvas Form ───
const ActivityFormOffcanvas = ({
  formType,
  editingItem,
  taskOwnerValue,
  onSave,
  onClose,
}) => {
  const isEdit = !!editingItem;
  const workOrderContext = useContext(AppContexts.WorkOrderContext);
  const getWorkOrderAttributesRef = useRef(workOrderContext.getAttributes);

  useEffect(() => {
    getWorkOrderAttributesRef.current = workOrderContext.getAttributes;
  }, [workOrderContext.getAttributes]);

  // Note state
  const [noteBody, setNoteBody] = useState("");

  // Task state
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const [taskDueAt, setTaskDueAt] = useState("");
  const [taskStatus, setTaskStatus] = useState("new");
  const [taskAssignees, setTaskAssignees] = useState([]);

  const taskAssignerOptions = useMemo(() => {
    const assigners = workOrderContext.attributes?.assigners || {};
    const recipients = (assigners.recipients || [])
      .map((recipient) => ({
        label: `${recipient.first_name || ""} ${recipient.last_name || ""}`.trim()
          || recipient.name
          || recipient.email
          || `Staff ${recipient.recipient_id}`,
        value: `recipient_${recipient.recipient_id}`,
      }))
      .filter((option) => option.value !== taskOwnerValue)
      .sort((a, b) => a.label.localeCompare(b.label));
    const contacts = (assigners.contacts || [])
      .map((contact) => {
        const name = `${contact.first_name || ""} ${contact.last_name || ""}`.trim()
          || contact.email
          || `Contact ${contact.id}`;
        const category = contact.category
          ? `${contact.category.charAt(0).toUpperCase()}${contact.category.slice(1)}`
          : "";
        return {
          label: category ? `${name} (${category})` : name,
          value: `contact_${contact.id}`,
        };
      })
      .filter((option) => option.value !== taskOwnerValue)
      .sort((a, b) => a.label.localeCompare(b.label));

    return [
      { label: "Staff", options: recipients },
      { label: "Contact", options: contacts },
    ];
  }, [taskOwnerValue, workOrderContext.attributes]);

  useEffect(() => {
    const activeType = formType || editingItem?.activity_type;
    if (activeType !== "task") return;
    getWorkOrderAttributesRef.current?.(undefined, ["assigners"]);
  }, [formType, editingItem?.activity_type, editingItem?.id]);

  // Call state
  const [callDirection, setCallDirection] = useState("outbound");
  const [callSummary, setCallSummary] = useState("");
  const [callOutcome, setCallOutcome] = useState("");
  const [callDuration, setCallDuration] = useState("");

  // Event state
  const [eventTitle, setEventTitle] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [eventDescription, setEventDescription] = useState("");

  // Email state
  const [emailDirection, setEmailDirection] = useState("outbound");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailSentAt, setEmailSentAt] = useState("");

  // Populate fields when editing
  useEffect(() => {
    if (editingItem) {
      if (editingItem.activity_type === "note" || formType === "note") {
        setNoteBody(editingItem.body || editingItem.title || "");
      }
      if (editingItem.activity_type === "task" || formType === "task") {
        setTaskTitle(editingItem.title || "");
        setTaskDescription(editingItem.description || "");
        setTaskDueAt(editingItem.due_at ? editingItem.due_at.split("T")[0] : "");
        setTaskStatus(editingItem.status === "open" ? "new" : editingItem.status || "new");
        setTaskAssignees(
          (editingItem.work_order_assignees || [])
            .filter((assignee) => assignee.assignment_role !== "owner")
            .map((assignee) => `${assignee.assignee_type}_${assignee.assignee_id}`),
        );
      }
      if (editingItem.activity_type === "call" || formType === "call") {
        setCallDirection(editingItem.direction || "outbound");
        setCallSummary(editingItem.summary || editingItem.title || "");
        setCallOutcome(editingItem.outcome || "");
        setCallDuration(editingItem.duration_seconds ? String(editingItem.duration_seconds) : "");
      }
      if (editingItem.activity_type === "email" || formType === "email") {
        setEmailDirection(editingItem.direction || "outbound");
        setEmailSubject(editingItem.subject || editingItem.title || "");
        setEmailTo(editingItem.to_email || "");
        setEmailCc(editingItem.cc_email || "");
        setEmailBody(editingItem.body || editingItem.description || "");
        // datetime-local needs "YYYY-MM-DDTHH:MM" in *local* time. Convert
        // the stored UTC instant to the user's local clock so it renders
        // the same value they originally entered.
        if (editingItem.sent_at) {
          const d = new Date(editingItem.sent_at);
          if (!isNaN(d.getTime())) {
            const pad = (n) => String(n).padStart(2, "0");
            setEmailSentAt(
              `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
                d.getDate(),
              )}T${pad(d.getHours())}:${pad(d.getMinutes())}`,
            );
          } else {
            setEmailSentAt("");
          }
        } else {
          setEmailSentAt("");
        }
      }
      if (editingItem.activity_type === "event" || formType === "event") {
        setEventTitle(editingItem.title || "");
        setEventDate(editingItem.event_date ? editingItem.event_date.split("T")[0] : "");
        setEventDescription(editingItem.description || "");
      }
    } else {
      resetAll();
    }
  }, [editingItem, formType]);

  const resetAll = () => {
    setNoteBody("");
    setTaskTitle("");
    setTaskDescription("");
    setTaskDueAt("");
    setTaskStatus("new");
    setTaskAssignees([]);
    setCallDirection("outbound");
    setCallSummary("");
    setCallOutcome("");
    setCallDuration("");
    setEmailDirection("outbound");
    setEmailSubject("");
    setEmailTo("");
    setEmailCc("");
    setEmailBody("");
    setEmailSentAt("");
    setEventTitle("");
    setEventDate("");
    setEventDescription("");
  };

  const handleSubmit = () => {
    const type = formType || editingItem?.activity_type;
    if (!type) return;

    let data = {};
    if (type === "note") {
      if (!noteBody.trim()) {
        UIkit.notification("Note body is required", "warning");
        return;
      }
      data = { body: noteBody.trim() };
    } else if (type === "task") {
      if (!taskTitle.trim()) {
        UIkit.notification("Task title is required", "warning");
        return;
      }
      data = {
        title: taskTitle.trim(),
        description: taskDescription.trim(),
        due_at: taskDueAt || null,
        status: taskStatus,
        assigners: taskAssignees.map((value) => {
          const separator = value.indexOf("_");
          return {
            type: value.slice(0, separator),
            id: value.slice(separator + 1),
            assignment_role: "assignee",
          };
        }),
      };
    } else if (type === "call") {
      if (!callSummary.trim()) {
        UIkit.notification("Call summary is required", "warning");
        return;
      }
      data = {
        summary: callSummary.trim(),
        direction: callDirection,
        outcome: callOutcome,
        duration_seconds: callDuration ? parseInt(callDuration, 10) : null,
      };
    } else if (type === "email") {
      if (!emailSubject.trim() && !emailBody.trim()) {
        UIkit.notification("Email subject or body is required", "warning");
        return;
      }
      // datetime-local provides a naive local string ("2026-06-05T14:00").
      // Convert to a true UTC ISO so the backend doesn't double-shift the
      // value (which previously made 2:00 PM display as 10:00 AM).
      let sentAtIso = null;
      if (emailSentAt) {
        const d = new Date(emailSentAt);
        if (!isNaN(d.getTime())) sentAtIso = d.toISOString();
      }
      data = {
        direction: emailDirection,
        subject: emailSubject.trim(),
        to_email: emailTo.trim(),
        cc_email: emailCc.trim(),
        body: emailBody.trim(),
        sent_at: sentAtIso,
      };
    } else if (type === "event") {
      if (!eventTitle.trim() || !eventDate) {
        UIkit.notification("Event title and date are required", "warning");
        return;
      }
      data = {
        title: eventTitle.trim(),
        event_date: eventDate,
        description: eventDescription.trim(),
      };
    }

    if (isEdit) data.id = editingItem.id;
    onSave(type, data, isEdit);
  };

  const activeType = formType || editingItem?.activity_type;
  const formTitle = isEdit
    ? `Edit ${ACTIVITY_LABELS[activeType] || "Activity"}`
    : `Add ${ACTIVITY_LABELS[activeType] || "Activity"}`;

  return (
    <div
      id="offcanvas-crm-activity-form"
      className="uk-modal-full hms-modals uk-modal uk-open uk-dark"
      uk-offcanvas="flip: true; overlay: true"
    >
      <div className="uk-offcanvas-bar">
        <div className="hms-modals-main">
          <button
            className="uk-offcanvas-close uk-close-large"
            type="button"
            uk-close="true"
            onClick={onClose}
          ></button>
          <div className="uk-modal-header">
            <h2 className="uk-modal-title">{formTitle}</h2>
          </div>
          <div className="uk-modal-body">
            {/* Note Form */}
            {activeType === "note" && (
              <div>
                <div className="uk-margin">
                  <label className="uk-form-label">Note</label>
                  <textarea
                    className="uk-textarea"
                    rows={5}
                    placeholder="Enter note…"
                    value={noteBody}
                    onChange={(e) => setNoteBody(e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Call Form */}
            {activeType === "task" && (
              <div>
                <div className="uk-margin">
                  <label className="uk-form-label">Title*</label>
                  <input
                    className="uk-input"
                    type="text"
                    required
                    aria-required="true"
                    placeholder="Task title"
                    value={taskTitle}
                    onChange={(e) => setTaskTitle(e.target.value)}
                  />
                </div>
                <div className="uk-margin">
                  <label className="uk-form-label">Due Date</label>
                  <input
                    className="uk-input"
                    type="date"
                    value={taskDueAt}
                    onChange={(e) => setTaskDueAt(e.target.value)}
                  />
                </div>
                <div className="uk-margin">
                  <label className="uk-form-label">Assign To</label>
                  <Select
                    className="crm-task-assignee-select"
                    mode="multiple"
                    optionFilterProp="label"
                    allowClear
                    style={{ width: "100%" }}
                    placeholder="Select a Team Member"
                    value={taskAssignees}
                    onChange={setTaskAssignees}
                    options={taskAssignerOptions}
                    getPopupContainer={() => document.body}
                  />
                </div>
                <div className="uk-margin">
                  <label className="uk-form-label">Status</label>
                  <div className="uk-inline uk-width-1-1">
                    <div uk-form-custom="target: > * > span:first-child" className="uk-form-custom uk-width-1-1">
                      <select
                        value={taskStatus}
                        onChange={(e) => setTaskStatus(e.target.value)}
                      >
                        <option value="new">New</option>
                        <option value="started">Started</option>
                        <option value="completed">Completed</option>
                        <option value="closed">Closed</option>
                      </select>
                      <button className="uk-button uk-button-default uk-width-1-1" type="button" tabIndex="-1">
                        <span>{{new:"New",started:"Started",completed:"Completed",closed:"Closed"}[taskStatus] || "New"}</span>
                        <span uk-icon="icon: chevron-down"></span>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="uk-margin">
                  <label className="uk-form-label">Description</label>
                  <textarea
                    className="uk-textarea"
                    rows={3}
                    placeholder="Task description"
                    value={taskDescription}
                    onChange={(e) => setTaskDescription(e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Call Form */}
            {activeType === "call" && (
              <div>
                <div className="uk-margin">
                  <label className="uk-form-label">Direction</label>
                  <div className="uk-inline uk-width-1-1">
                    <div uk-form-custom="target: > * > span:first-child" className="uk-form-custom uk-width-1-1">
                      <select
                        value={callDirection}
                        onChange={(e) => setCallDirection(e.target.value)}
                      >
                        <option value="outbound">Outbound</option>
                        <option value="inbound">Inbound</option>
                      </select>
                      <button className="uk-button uk-button-default uk-width-1-1" type="button" tabIndex="-1">
                        <span>{callDirection === "inbound" ? "Inbound" : "Outbound"}</span>
                        <span uk-icon="icon: chevron-down"></span>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="uk-margin">
                  <label className="uk-form-label">Summary</label>
                  <textarea
                    className="uk-textarea"
                    rows={3}
                    placeholder="Call summary"
                    value={callSummary}
                    onChange={(e) => setCallSummary(e.target.value)}
                  />
                </div>
                <div className="uk-margin">
                  <label className="uk-form-label">Outcome</label>
                  <div className="uk-inline uk-width-1-1">
                    <div uk-form-custom="target: > * > span:first-child" className="uk-form-custom uk-width-1-1">
                      <select
                        value={callOutcome}
                        onChange={(e) => setCallOutcome(e.target.value)}
                      >
                        <option value="">Select outcome…</option>
                        <option value="connected">Connected</option>
                        <option value="no_answer">No Answer</option>
                        <option value="left_voicemail">Left Voicemail</option>
                        <option value="busy">Busy</option>
                      </select>
                      <button className="uk-button uk-button-default uk-width-1-1" type="button" tabIndex="-1">
                        <span>{{"":"Select outcome…",connected:"Connected",no_answer:"No Answer",left_voicemail:"Left Voicemail",busy:"Busy"}[callOutcome] || "Select outcome…"}</span>
                        <span uk-icon="icon: chevron-down"></span>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="uk-margin">
                  <label className="uk-form-label">Duration (seconds)</label>
                  <input
                    className="uk-input"
                    type="number"
                    min="0"
                    placeholder="e.g. 120"
                    value={callDuration}
                    onChange={(e) => setCallDuration(e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Event Form */}
            {activeType === "event" && (
              <div>
                <div className="uk-margin">
                  <label className="uk-form-label">Title</label>
                  <input
                    className="uk-input"
                    type="text"
                    placeholder="Event title"
                    value={eventTitle}
                    onChange={(e) => setEventTitle(e.target.value)}
                  />
                </div>
                <div className="uk-margin">
                  <label className="uk-form-label">Date</label>
                  <input
                    className="uk-input"
                    type="date"
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                  />
                </div>
                <div className="uk-margin">
                  <label className="uk-form-label">Description</label>
                  <textarea
                    className="uk-textarea"
                    rows={3}
                    placeholder="Event description"
                    value={eventDescription}
                    onChange={(e) => setEventDescription(e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Email Form */}
            {activeType === "email" && (
              <div>
                <div className="uk-margin">
                  <label className="uk-form-label">Direction</label>
                  <div className="uk-inline uk-width-1-1">
                    <div uk-form-custom="target: > * > span:first-child" className="uk-form-custom uk-width-1-1">
                      <select
                        value={emailDirection}
                        onChange={(e) => setEmailDirection(e.target.value)}
                      >
                        <option value="outbound">Outbound</option>
                        <option value="inbound">Inbound</option>
                      </select>
                      <button className="uk-button uk-button-default uk-width-1-1" type="button" tabIndex="-1">
                        <span>{emailDirection === "inbound" ? "Inbound" : "Outbound"}</span>
                        <span uk-icon="icon: chevron-down"></span>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="uk-margin">
                  <label className="uk-form-label">Subject</label>
                  <input
                    className="uk-input"
                    type="text"
                    placeholder="Email subject"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                  />
                </div>
                <div className="uk-margin">
                  <label className="uk-form-label">To</label>
                  <input
                    className="uk-input"
                    type="text"
                    placeholder="recipient@example.com"
                    value={emailTo}
                    onChange={(e) => setEmailTo(e.target.value)}
                  />
                </div>
                <div className="uk-margin">
                  <label className="uk-form-label">CC</label>
                  <input
                    className="uk-input"
                    type="text"
                    placeholder="cc@example.com"
                    value={emailCc}
                    onChange={(e) => setEmailCc(e.target.value)}
                  />
                </div>
                <div className="uk-margin">
                  <label className="uk-form-label">Sent At</label>
                  <input
                    className="uk-input"
                    type="datetime-local"
                    value={emailSentAt}
                    onChange={(e) => setEmailSentAt(e.target.value)}
                  />
                </div>
                <div className="uk-margin">
                  <label className="uk-form-label">Body</label>
                  <textarea
                    className="uk-textarea"
                    rows={6}
                    placeholder="Email body"
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Save / Cancel */}
            <div className="crm-activity-form-actions uk-margin-top">
              <button
                type="button"
                className="uk-button hms-btn hms-red-btn"
                onClick={handleSubmit}
              >
                {isEdit ? "Update" : "Save"}
              </button>
              <button
                type="button"
                className="uk-button hms-btn hms-white-btn"
                onClick={() => {
                  UIkit.offcanvas("#offcanvas-crm-activity-form").hide();
                  onClose();
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CRMActivityPanel;
